import { TextField } from '../../mui/common/TextField.tsx';
import { sanitizeNumericInput } from 'utils/validations/sanitizeNumericInput.ts';
import { TABLE_CELL_TYPE } from '../../../types/enums.ts';
import type { Control, ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useState, type ReactElement } from 'react';
import type { DataSchema, FlattenedJoinedAgendaRecord, JoinedColumnSource, Schema } from '../../../types';
import { SelectEditable } from './cellComponents/editable/SelectEditable.tsx';
import { DateItem } from '../../user/Records/components/DateItem.tsx';
import { CheckboxEditable } from './cellComponents/editable/CheckboxEditable.tsx';
import type { TableRowDataSchema } from '../../../types/table.ts';
import type { Row } from '@tanstack/react-table';
import { TablesItemEditable } from './cellComponents/editable/TableItemEditable.tsx';
import { UsersItemEditable } from './cellComponents/editable/UsersItemEditable.tsx';
import { RecordsItemEditable } from './cellComponents/editable/RecordsItemEditable.tsx';
import { FileUploadsItemEditable } from './cellComponents/editable/FileUploadsItemEditable.tsx';
import type { ColumnOverflowMode, FormItemDefinition, FormItemUiDefinition } from '../../../types/formItem.ts';
import { formatFormItemNumberValue } from 'utils/helios/numberFormatting';
import { useLocaleCodeIsoNorma } from 'utils/hooks';
import type { DocumentEditingOptions } from 'types/documentEditing';

export interface EditableComponentFactoryProps<T extends keyof P, P extends DataSchema, K extends FieldValues> {
  documentEditing?: DocumentEditingOptions;
  type: TABLE_CELL_TYPE;
  agendaId?: string;
  row: Row<Schema<T, P>>;
  rowDataSchema: TableRowDataSchema;
  registerName: Path<K>;
  control: Control<K>;
  background?: 'white' | 'transparent';
  disabled?: boolean;
  formItemUiDefinition?: FormItemUiDefinition | null;
  overflowMode?: ColumnOverflowMode;
}

export const getFormFieldName = <T extends keyof P, P extends DataSchema, K extends FieldValues>(
  row: Row<Schema<T, P>>,
  columnId: string
): Path<K> => {
  return getFormFieldPath(row.original.identifier, columnId);
};

export const getFormFieldPath = <K extends FieldValues>(rowId: string, columnId: string): Path<K> => {
  return `${rowId}.${columnId}` as Path<K>;
};

export const parseFormFieldPath = (name: string): { rowId: string; columnId: string } | null => {
  const separator = name.indexOf('.');
  if (separator <= 0 || separator >= name.length - 1) return null;

  return { rowId: name.slice(0, separator), columnId: name.slice(separator + 1) };
};

export const createEditableComponentFactory = <T extends keyof P, P extends DataSchema, K extends FieldValues>(
  props: EditableComponentFactoryProps<T, P, K>
): ReactElement | null => {
  const formItemDefinition = props.rowDataSchema.formItemDefinition;

  const formItemType = formItemDefinition?.type;

  switch (formItemType) {
    case 'RadioButton':
      return createSelect(props);
    case 'Tables':
      return createTables(props);
    case 'Records':
      return createRecords(props);

    default:
      switch (props.type) {
        case TABLE_CELL_TYPE.ID:
          return null;
        case TABLE_CELL_TYPE.TEXT:
        case TABLE_CELL_TYPE.NUMBER:
        case TABLE_CELL_TYPE.PHONE_NUMBER:
        case TABLE_CELL_TYPE.GUID:
        case TABLE_CELL_TYPE.EMAIL:
          return createTextField(props);

        case TABLE_CELL_TYPE.DATE:
          return createDate(props);

        case TABLE_CELL_TYPE.CHECKBOX:
          return createCheckbox(props);

        case TABLE_CELL_TYPE.USERS:
          return createUsers(props);

        case TABLE_CELL_TYPE.FILE_UPLOAD:
          return createFileUpload(props);

        case TABLE_CELL_TYPE.COMMENT:
        case TABLE_CELL_TYPE.NAME_ARRAY:
        case TABLE_CELL_TYPE.HELIOS_BUTTON:
        case TABLE_CELL_TYPE.BUTTON:
          return null;
        default:
          return createTextField(props);
      }
  }
};

const createTextField = <T extends keyof P, P extends DataSchema, K extends FieldValues>(
  props: EditableComponentFactoryProps<T, P, K>
): ReactElement => {
  const { rowDataSchema, control, registerName } = props;
  const originalType = rowDataSchema.originalType;
  const isNumber = props.rowDataSchema.formItemDefinition?.type === 'Number';

  return (
    <Controller
      control={control}
      name={registerName}
      render={({ field, fieldState: { error } }) =>
        isNumber ? (
          <NumberCellField
            field={field}
            error={error}
            disabled={props.disabled || originalType === 'Computed'}
            background={props.background}
            definition={rowDataSchema.formItemDefinition}
            overflowMode={props.overflowMode}
            formItemUiDefinition={props.formItemUiDefinition}
          />
        ) : (
          <TextField
            {...field}
            sx={{ paddingTop: 0 }}
            type="text"
            multiline={props.overflowMode === 'Wrap'}
            disableOverflowTooltip={props.overflowMode !== undefined}
            error={Boolean(error)}
            helperText={error?.message}
            disabled={props.disabled || originalType === 'Computed'}
            dataTestId="edit-field"
            background={props.background}
            fullWidth
            formItemUiDefinition={props.formItemUiDefinition}
            onChange={(event) => field.onChange(event.target.value)}
          />
        )
      }
    />
  );
};

const NumberCellField = <K extends FieldValues>({
  field,
  error,
  disabled,
  background,
  definition,
  overflowMode,
  formItemUiDefinition,
}: {
  field: ControllerRenderProps<K, Path<K>>;
  error?: { message?: string };
  disabled?: boolean;
  background?: 'white' | 'transparent';
  definition?: FormItemDefinition;
  overflowMode?: ColumnOverflowMode;
  formItemUiDefinition?: FormItemUiDefinition | null;
}) => {
  const numberDefinition = definition?.Number ?? definition?.number;
  const locale = useLocaleCodeIsoNorma();
  const [isEditing, setIsEditing] = useState(false);
  const formattedValue = formatFormItemNumberValue(field.value, locale, {
    thousandsSeparator: numberDefinition?.thousandsSeparator,
    roundTo: numberDefinition?.roundTo,
    mathRound: numberDefinition?.mathRound,
  });

  return (
    <TextField
      {...field}
      sx={{ paddingTop: 0 }}
      value={!isEditing && formattedValue !== null ? formattedValue : (field.value ?? '')}
      type="text"
      multiline={overflowMode === 'Wrap'}
      disableOverflowTooltip={overflowMode !== undefined}
      suffix={numberDefinition?.unit ?? undefined}
      error={Boolean(error)}
      helperText={error?.message}
      disabled={disabled}
      dataTestId="edit-field"
      background={background}
      fullWidth
      formItemUiDefinition={formItemUiDefinition}
      onFocus={() => setIsEditing(true)}
      onBlur={() => {
        setIsEditing(false);
        field.onBlur();
      }}
      onChange={(event) => field.onChange(sanitizeNumericInput(event.target.value))}
      slotProps={{ htmlInput: { inputMode: 'decimal' as const } }}
    />
  );
};

const createSelect = <T extends keyof P, P extends DataSchema, K extends FieldValues>(
  props: EditableComponentFactoryProps<T, P, K>
): ReactElement => {
  const { rowDataSchema, control, registerName } = props;
  const formItemDefinition = rowDataSchema.formItemDefinition;

  return (
    <Controller
      control={control}
      name={registerName}
      render={({ field, fieldState: { error } }) => (
        <SelectEditable
          items={formItemDefinition?.radioButton?.items}
          error={Boolean(error)}
          helperText={error?.message}
          fullWidth
          disabled={props.disabled}
          background={props.background}
          formItemUiDefinition={props.formItemUiDefinition ?? undefined}
          {...field}
        />
      )}
    />
  );
};

const createDate = <T extends keyof P, P extends DataSchema, K extends FieldValues>(props: EditableComponentFactoryProps<T, P, K>) => {
  return (
    <DateItem
      registerName={props.registerName}
      definition={props.rowDataSchema.formItemDefinition}
      background={props.background}
      disabled={props.disabled}
      formItemUiDefinition={props.formItemUiDefinition ?? undefined}
    />
  );
};

const createCheckbox = <T extends keyof P, P extends DataSchema, K extends FieldValues>(props: EditableComponentFactoryProps<T, P, K>) => {
  const { control, registerName, disabled, formItemUiDefinition } = props;

  return (
    <Controller
      control={control}
      name={registerName}
      render={({ field }) => <CheckboxEditable {...field} disabled={disabled} formItemUiDefinition={formItemUiDefinition ?? undefined} />}
    />
  );
};

const createTables = <T extends keyof P, P extends DataSchema, K extends FieldValues>(
  props: EditableComponentFactoryProps<T, P, K>
): ReactElement => {
  return (
    <TablesItemEditable
      id={props.registerName}
      definition={props.rowDataSchema.formItemDefinition}
      agendaId={props.agendaId}
      disabled={props.disabled}
    />
  );
};

const createUsers = <T extends keyof P, P extends DataSchema, K extends FieldValues>(props: EditableComponentFactoryProps<T, P, K>): ReactElement => {
  return (
    <UsersItemEditable
      id={props.registerName}
      definition={props.rowDataSchema.formItemDefinition}
      agendaId={props.agendaId}
      disabled={props.disabled}
    />
  );
};

type JoinedTableRow = Partial<Pick<FlattenedJoinedAgendaRecord, 'id' | 'nestedRecord' | 'secondLevelNestedRecord'>>;

const owningRecord = (row: JoinedTableRow, level?: JoinedColumnSource['level']) => {
  const record = level === 'secondLevel' ? row.secondLevelNestedRecord : level === 'nested' ? row.nestedRecord : null;

  return record?.id ? record : null;
};

const recordsPickerTarget = (
  row: JoinedTableRow,
  joinedColumn: JoinedColumnSource | undefined,
  fallbackRecordId: string,
  fallbackAgendaId?: string
) => {
  const record = owningRecord(row, joinedColumn?.level);
  if (record) return { recordId: record.id, agendaId: record.agendaId || joinedColumn?.agendaId || fallbackAgendaId };

  const isJoinedRow = 'nestedRecord' in row || 'secondLevelNestedRecord' in row;
  if (!isJoinedRow) return { recordId: fallbackRecordId, agendaId: fallbackAgendaId };

  return { recordId: row.id || fallbackRecordId, agendaId: fallbackAgendaId };
};

const createRecords = <T extends keyof P, P extends DataSchema, K extends FieldValues>(props: EditableComponentFactoryProps<T, P, K>) => {
  const { row, rowDataSchema, formItemUiDefinition } = props;
  const { joinedColumn } = rowDataSchema as TableRowDataSchema & { joinedColumn?: JoinedColumnSource };
  const target = recordsPickerTarget(row.original, joinedColumn, row.original.identifier, props.agendaId);

  return (
    <RecordsItemEditable
      id={props.registerName}
      recordId={target.recordId}
      agendaId={target.agendaId}
      definition={rowDataSchema.formItemDefinition}
      disabled={props.disabled}
      formItemUiDefinition={formItemUiDefinition}
    />
  );
};

const createFileUpload = <T extends keyof P, P extends DataSchema, K extends FieldValues>(
  props: EditableComponentFactoryProps<T, P, K>
): ReactElement => {
  return (
    <FileUploadsItemEditable
      documentEditing={props.documentEditing}
      id={props.registerName}
      definition={props.rowDataSchema.formItemDefinition}
      disabled={props.disabled}
      background={props.background}
      formItemUiDefinition={props.formItemUiDefinition}
    />
  );
};
